import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AppShell from '../components/AppShell';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Icon from '../components/Icon';
import { apiFetch } from '../config/api';
import { usePlanos, nivelDoPlano } from '../config/planos';
import { useIdioma } from '../i18n';
import { useFormato } from '../i18n/formato';
import { priceIdFor, MESES_GRATIS_NO_ANUAL, mensalEquivalente } from '../config/stripePrices';
import '../styles/Plans.css';

/**
 * Planos — a mesma rota para quem está logado e para quem não está.
 *
 * `/plans` é pública: é o destino de "See plans" na home e também o atalho
 * "Plan and Usage" do painel. Antes ela ignorava a sessão, então quem já era
 * cliente via quatro botões "Get Started" idênticos, sem qualquer indicação de
 * qual plano já tinha. Agora a moldura muda — AppShell no app, Header público
 * fora dele — e o cartão do plano vigente aparece marcado.
 *
 * A lista de planos vem de `config/planos`, derivada do `planConfigs` que o
 * backend aplica. Esta tela era a que mais divergia: anunciava limites que o
 * produto não dá e um plano "Enterprise" inexistente.
 */

/* Voltar para Basic é cancelar a assinatura — Basic é o plano sem cobrança, e
   o que o backend sabe fazer é `cancel_at_period_end`. Chamar isso de
   "Downgrade" e abrir um checkout de $0 criaria uma segunda assinatura em
   paralelo à que continua cobrando. */
const acaoPara = (nome, atual) => {
  /* Business antes de tudo: não tem preço de tabela nem price ID. Deslogado,
     este cartão dizia "Get Started" e o clique caía em "plano indisponível". */
  if (nome === 'Business') return { chave: 'planosTela.faleConosco', tipo: 'contato' };
  if (!atual) return { chave: 'planosTela.comecar', tipo: 'checkout' };
  if (nome === atual) return { chave: 'planosTela.planoAtual', tipo: 'atual' };
  if (nome === 'Basic') return { chave: 'planosTela.cancelarAssinatura', tipo: 'cancelar' };
  return {
    chave: nivelDoPlano(nome) > nivelDoPlano(atual) ? 'planosTela.melhorar' : 'planosTela.rebaixar',
    tipo: 'checkout'
  };
};

const Plans = ({ user }) => {
  const { t } = useIdioma();
  const { data, moeda } = useFormato();
  const planos = usePlanos();
  const [anual, setAnual] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');
  const [faturas, setFaturas] = useState(null);

  const planoAtual = user?.plan;

  useEffect(() => {
    if (!user) return;
    apiFetch('/payments/history')
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => setFaturas(json?.payments?.slice(0, 5) || []))
      .catch(() => setFaturas([]));
  }, [user]);

  const irParaCheckout = async (nome) => {
    const priceId = priceIdFor(nome, anual ? 'annual' : 'monthly');
    if (!priceId) {
      setErro(t('erros.planoIndisponivel'));
      return;
    }

    setCarregando(true);
    setErro('');
    try {
      const resposta = await apiFetch('/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ priceId, fromPlans: true })
      });
      const dados = await resposta.json();
      if (resposta.ok && dados.url) {
        window.location.href = dados.url;
        return;
      }
      setErro(dados.error || t('erros.requisicao'));
    } catch {
      setErro(t('erros.conexao'));
    } finally {
      setCarregando(false);
    }
  };

  const cancelar = async () => {
    if (!window.confirm(t('conta.confirmarCancelamento'))) return;

    setCarregando(true);
    setErro('');
    try {
      const resposta = await apiFetch('/cancel-subscription', { method: 'POST' });
      const dados = await resposta.json();
      if (!resposta.ok) throw new Error(dados.error);
      setAviso(dados.message || t('planosTela.assinaturaCancelada'));
    } catch (e) {
      setErro(e.message || t('erros.cancelamentoAssinatura'));
    } finally {
      setCarregando(false);
    }
  };

  const executar = (nome, tipo) => {
    if (carregando) return;
    if (tipo === 'checkout') return irParaCheckout(nome);
    if (tipo === 'cancelar') return cancelar();
    if (tipo === 'contato') window.location.href = '/#contact';
  };

  const grade = (
    <>
      <div className="plans-ciclo">
        <div className="sp-seg" role="group" aria-label={t('precos.ciclo')}>
          <button
            type="button"
            className="sp-seg-opt"
            aria-pressed={!anual}
            onClick={() => setAnual(false)}
          >
            {t('precos.mensal')}
          </button>
          <button
            type="button"
            className="sp-seg-opt"
            aria-pressed={anual}
            onClick={() => setAnual(true)}
          >
            {t('precos.anual')}
          </button>
        </div>
        <span className="sp-tag sp-tag-ok">
          {t('precos.mesesGratis', { n: MESES_GRATIS_NO_ANUAL })}
        </span>
      </div>

      {erro && (
        <p className="sp-alert sp-alert-crit" role="alert">
          {erro}
        </p>
      )}
      {aviso && (
        <p className="sp-alert sp-alert-ok" role="status">
          {aviso}
        </p>
      )}

      <div className="plans-grid">
        {planos.map((p) => {
          const atual = p.nome === planoAtual;
          const acao = acaoPara(p.nome, planoAtual);
          const gratuito = p.preco?.mensal === 0;
          const valor = p.contato
            ? t('precos.personalizado')
            : anual && !gratuito
              ? moeda(Math.round(mensalEquivalente(p.nome) * 100), 'usd')
              : moeda(p.preco.mensal * 100, 'usd');

          return (
            <article
              className={`sp-card plan-mini ${atual ? 'is-current' : ''}`.trim()}
              key={p.nome}
            >
              {atual && (
                <span className="sp-tag sp-tag-accent plan-mini-flag">{t('planosTela.atual')}</span>
              )}
              <b>{p.nome}</b>
              <div className="sp-metric">
                {valor}
                {!p.contato && (
                  <small>
                    {anual && !gratuito
                      ? t('precos.anualDetalhe', { valor: p.preco.anual })
                      : t('precos.porMes')}
                  </small>
                )}
              </div>
              <span>{p.descricao}</span>

              <ul className="plan-mini-recursos">
                {p.recursos.map((r) => (
                  <li key={r}>
                    <Icon name="check" size={12} />
                    {r}
                  </li>
                ))}
              </ul>

              <button
                type="button"
                className={`sp-btn ${atual ? 'sp-btn-secondary' : 'sp-btn-primary'}`}
                disabled={carregando || acao.tipo === 'atual'}
                onClick={() => executar(p.nome, acao.tipo)}
              >
                {carregando ? t('precos.carregando') : t(acao.chave)}
              </button>
            </article>
          );
        })}
      </div>
    </>
  );

  if (!user) {
    return (
      <div className="plans-publico">
        <Header />
        <main className="sp-wrap">
          <div className="plans-publico-head">
            <h1>{t('planosTela.publicoTitulo')}</h1>
            <p>{t('planosTela.publicoChamada')}</p>
          </div>
          <div className="plans-publico-corpo">{grade}</div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <AppShell user={user} crumbs={[t('menu.conta'), t('menu.planos')]}>
      <div className="app-page-head">
        <div>
          <h1>{t('planosTela.titulo')}</h1>
          <p>
            {planoAtual}
            {t(
              planoAtual === 'Basic' ? 'planosTela.gratuitoParaSempre' : 'planosTela.cobradoStripe'
            )}
          </p>
        </div>
      </div>

      {grade}

      <section className="sp-card plans-invoices">
        <div className="plans-invoices-head">
          <h2>{t('planosTela.cobrancasRecentes')}</h2>
          <Link to="/payments">{t('planosTela.historicoCompleto')}</Link>
        </div>

        {faturas === null && <p className="sp-muted">{t('comum.carregando')}</p>}

        {faturas?.length === 0 && (
          <p className="plans-vazio">
            {t(planoAtual === 'Basic' ? 'planosTela.basicSemCobranca' : 'planosTela.semCobrancas')}
          </p>
        )}

        {faturas?.length > 0 && (
          <table className="sp-table plans-table">
            <thead>
              <tr>
                <th>{t('pagamentos.data')}</th>
                <th>{t('pagamentos.descricao')}</th>
                <th className="sp-num">{t('pagamentos.valor')}</th>
                <th>{t('pagamentos.status')}</th>
              </tr>
            </thead>
            <tbody>
              {faturas.map((f) => (
                <tr key={f.id}>
                  <td className="sp-mono sp-muted-2">{data(f.created * 1000)}</td>
                  <td>{f.plan_name || f.description || '—'}</td>
                  <td className="sp-num">{moeda(f.amount, f.currency)}</td>
                  <td>
                    <span className={`sp-tag sp-tag-${f.paid ? 'ok' : 'crit'}`}>
                      {f.paid ? t('pagamentos.pago') : f.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </AppShell>
  );
};

export default Plans;
